import { isSuccessResponse } from '../types/api'
import type { ErrorResponse, SuccessResponse } from '../types/api'
import { APIError } from './_error/APIError'

type APIMethods = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'

export const getHostname = () => {
  const env = useRuntimeConfig().public.environment
  if (env === 'REL')
    return 'https://release.snackprompt.com'
  else if (env === 'DEV')
    return 'http://localhost:3000'
  else if (env === 'LOCAL')
    return 'http://localhost:3000'
  else
    return 'https://snackprompt.com'
}

export const APIErrorHandler = (err: any): ErrorResponse => {
  const resp = err.response?.data as ErrorResponse

  if (!resp) {
    return new APIError('Sorry, we were unable to perform this action', 500, {
      isToast: false,
      errorMessage: 'Sorry, we were unable to perform this action',
      status: 500,
    })
  }
  return new APIError(resp.errorMessage, resp.status ?? 500, resp, resp.isToast)
}

export const serverFetch = async <T>(path: string, options?: { method: APIMethods; body?: any; baseUrl?: string }): Promise<SuccessResponse<T> | ErrorResponse> => {
  const runtimeConfig = useRuntimeConfig()

  const headers = new Headers()
  headers.set('Sp-App', 'Web')

  let body: any

  if (options?.body && options.method !== 'GET')
    body = options.body

  return await $fetch<SuccessResponse<T>>(`${options?.baseUrl || runtimeConfig.public.apiUrl}${path}`, {
    method: options?.method || 'GET',
    body,
    headers,
  }).then(resp => resp).catch(APIErrorHandler)
}

export const APINativeFetch = async <T>(path: string, options: { method: APIMethods; body?: any; apiUrl?: string; signal?: AbortSignal }): Promise<SuccessResponse<T> | ErrorResponse> => {
  const runtimeConfig = useRuntimeConfig()
  const { firebaseUser } = useFirebaseAuth()

  const token = await firebaseUser.value?.getIdToken()

  const baseUrl = options.apiUrl || runtimeConfig.public.apiUrl

  const headers: HeadersInit = {
    'Authorization': token ? `Bearer ${token}` : '',
    'Content-Type': 'application/json',
    'Sp-App': 'Web',
  }

  let body

  if (options.method !== 'GET' && options.body)
    body = JSON.stringify(options.body)

  return await fetch(`${baseUrl}${path}`, {
    method: options.method,
    body,
    headers,
    signal: options.signal,
  }).then(resp => resp.json()).catch(APIErrorHandler)
}

export const APIFetch = async <T>(path: string, options: { method: APIMethods; body?: any; baseUrl?: string; headers?: Record<string, string | undefined>; isFormData?: boolean; signal?: AbortSignal; timeout?: number }): Promise<SuccessResponse<T> | ErrorResponse> => {
  const runtimeConfig = useRuntimeConfig()
  const { firebaseUser } = useFirebaseAuth()
  const tokenResult = await firebaseUser.value?.getIdToken()
  const token = tokenResult

  const headers = new Headers()
  headers.set('Authorization', token ? `Bearer ${token}` : '')
  headers.set('Sp-App', 'Web')

  let body
  if (options.method !== 'GET')
    body = options.isFormData ? options.body : JSON.stringify(options.body)

  let errorContext: any = null

  try {
    const data = await $fetch<SuccessResponse<T> | ErrorResponse>(`${options.baseUrl || runtimeConfig.public.apiUrl}${path}`, {
      method: options.method,
      body,
      signal: options.signal,
      headers,
      timeout: options.timeout,
      onRequestError: (context) => {
        errorContext = context
      },
      onResponseError: (context) => {
        errorContext = context
      },
    })

    return { ...data, status: 200 }
  }
  catch (error: any) {
    return APIErrorHandler({
      ...error,
      response: {
        ...errorContext?.response,
        data: errorContext?.response?._data,
        status: errorContext?.response?.status || 500,
      },
    })
  }
}

export const filterResponse = <T>(req: SuccessResponse<T> | ErrorResponse): null | T => {
  if (isSuccessResponse(req)) {
    return req.response
  }
  else {
    if (req.status === 500)
      throw req

    if (req.status === 409 && req.errorInfo?.includes('Stripe account is restricted'))
      return 'restricted' as T

    throw new APIError(req.errorMessage, req.status ?? 500, req.errorInfo)
  }
}
export const filterResponseBoolean = <T>(req: SuccessResponse<T> | ErrorResponse): boolean => isSuccessResponse(req)
